import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings'

/**
 * Notification permission state, collapsed to the three cases the login gate
 * cares about. Capacitor's 'prompt' and 'prompt-with-rationale' both map to
 * 'prompt' (we can still show the OS dialog); 'denied' means the OS will no
 * longer show the dialog and the user must re-enable from OS settings.
 */
export type NotificationPermissionState = 'granted' | 'prompt' | 'denied'

const mapPermission = (state: string): NotificationPermissionState => {
    switch (state) {
        case 'granted':
            return 'granted'
        case 'denied':
            return 'denied'
        default:
            return 'prompt'
    }
}

/**
 * Current notification permission. On web the login gate does not apply, so we
 * report 'granted' to make the gate a no-op there.
 */
export const checkNotificationPermission = async (): Promise<NotificationPermissionState> => {
    if (!Capacitor.isNativePlatform()) {
        return 'granted'
    }

    const { receive } = await PushNotifications.checkPermissions()
    return mapPermission(receive)
}

/**
 * Ask the OS for permission. Only shows the system dialog when the state is
 * still 'prompt'; once 'denied', the OS resolves immediately without a dialog.
 */
export const requestNotificationPermission = async (): Promise<NotificationPermissionState> => {
    if (!Capacitor.isNativePlatform()) {
        return 'granted'
    }

    const { receive } = await PushNotifications.requestPermissions()
    return mapPermission(receive)
}

/**
 * Deep-link to the app's notification settings (Android) / app settings (iOS,
 * the only screen Apple officially supports). No-op on web.
 */
export const openAppNotificationSettings = async (): Promise<void> => {
    if (!Capacitor.isNativePlatform()) {
        return
    }

    await NativeSettings.open({
        optionAndroid: AndroidSettings.AppNotification,
        optionIOS: IOSSettings.App,
    })
}
